import axios from 'axios';

interface FastApiDetailItem {
  msg?: string;
}

interface ErrorEnvelope {
  error?: {
    code?: string;
    message?: string;
  };
  detail?: string | FastApiDetailItem[];
  message?: string;
}

export function getApiErrorStatus(error: unknown): number | undefined {
  if (!axios.isAxiosError(error)) return undefined;
  return error.response?.status;
}

export function getRetryAfterSeconds(error: unknown): number | undefined {
  if (!axios.isAxiosError(error)) return undefined;

  const rawHeader = error.response?.headers?.['retry-after'];
  if (typeof rawHeader !== 'string' || !rawHeader.trim()) {
    return undefined;
  }

  const numericSeconds = Number(rawHeader);
  if (Number.isFinite(numericSeconds) && numericSeconds > 0) {
    return Math.ceil(numericSeconds);
  }

  const retryDate = Date.parse(rawHeader);
  if (Number.isNaN(retryDate)) {
    return undefined;
  }

  const secondsUntilRetry = Math.ceil((retryDate - Date.now()) / 1000);
  return secondsUntilRetry > 0 ? secondsUntilRetry : undefined;
}

export function getApiErrorCode(error: unknown): string | undefined {
  if (!axios.isAxiosError<ErrorEnvelope>(error)) return undefined;
  return error.response?.data?.error?.code;
}

export function getApiErrorMessage(error: unknown, fallback: string): string {
  if (!axios.isAxiosError<ErrorEnvelope>(error)) return fallback;

  const data = error.response?.data;
  if (!data) return fallback;

  if (typeof data.error?.message === 'string' && data.error.message.trim()) {
    return data.error.message;
  }

  if (typeof data.message === 'string' && data.message.trim()) {
    return data.message;
  }

  if (typeof data.detail === 'string' && data.detail.trim()) {
    return data.detail;
  }

  if (Array.isArray(data.detail)) {
    const firstMessage = data.detail.find((item) => item?.msg)?.msg;
    if (typeof firstMessage === 'string' && firstMessage.trim()) {
      return firstMessage;
    }
  }

  return fallback;
}
